import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router";

import { useLookups } from "../../api/hooks.ts";
import {
  useDismissNotifications,
  useMarkNotificationsSeen,
  useNotifications,
  type Notification,
  type NotificationKind,
} from "../../api/notifications.ts";
import { formatSince } from "../../domain/format.ts";
import { Icon, type IconName } from "../../ui/Icon.tsx";
import { usePermissions } from "../auth/permissions.ts";

/**
 * LA CAMPANA de la topbar, con su contador y su panel (ADR 0003, `renderNotif`).
 *
 * TRES DECISIONES, y las tres se notan al usarla:
 *
 * 1. **El texto lo escribe el servidor.** El singular y el plural dependen del
 *    recuento, y los mismos avisos tienen que decir lo mismo aqui y en cualquier
 *    cliente que venga despues. De `kind` salen solo el icono y el destino.
 * 2. **Cada aviso es un ENLACE a donde se resuelve.** Saber que tienes tres
 *    operaciones sin revisar y no tener desde donde ir a revisarlas es la mitad
 *    de un aviso. Al navegar, el panel se cierra: dejarlo abierto tapa la
 *    pantalla a la que acabas de llegar.
 * 3. **Sin avisos no se pinta un panel vacio.** Se dice que esta todo al dia,
 *    que es una respuesta; un hueco no lo es.
 *
 * Marcar como vistos NO resuelve nada y no lo finge: apaga el contador y los
 * avisos siguen en la lista mientras su condicion se cumpla. Un aviso
 * desaparece al revisar la operacion, no al mirar la campana.
 */

/**
 * De que tipo es cada aviso y a donde lleva.
 *
 * Se escribe como `Record<NotificationKind, ...>` a proposito: el dia que el
 * servidor anada un sexto tipo, esto deja de compilar. Con un `find` y un
 * respaldo, ese aviso nuevo saldria en el panel sin icono y sin destino, y nadie
 * se enteraria.
 */
const KINDS: Record<NotificationKind, { icon: IconName; to: string }> = {
  /* Una abierta se resuelve CERRANDOLA, y el cierre esta en su detalle: el
     listado filtrado es el camino mas corto que existe hasta ahi. */
  staleOpenTrades: { icon: "clock", to: "/trades?pendientes=abiertas" },
  tradesToReview: { icon: "edit", to: "/trades?pendientes=revision" },
  /* El diario se escribe en la revision semanal, que es la pantalla que ensena
     los siete dias y cual quedo en blanco. */
  missedJournalDays: { icon: "calendar", to: "/semana" },
  /* La llama es la marca de regla innegociable rota en toda la aplicacion
     (SPEC 16): el color fuerte mide disciplina, no dinero. */
  brokenMandatoryRules: { icon: "flame", to: "/trades?pendientes=innegociables" },
  stalePlannedTrades: { icon: "bookmark", to: "/trades?pendientes=planificadas" },
};

export function NotificationBell(): ReactNode {
  const location = useLocation();
  const notifications = useNotifications();
  const markSeen = useMarkNotificationsSeen();
  const dismiss = useDismissNotifications();
  const lookups = useLookups();
  const { viewingOther } = usePermissions();

  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  const timeZone = lookups.data?.app.timeZone ?? "America/Guatemala";
  const items = notifications.data?.items ?? [];
  const unread = notifications.data?.unreadCount ?? 0;

  /* Al navegar se cierra. Va por `pathname` Y por la query, porque dos avisos
     distintos llevan al mismo `/trades` con otro filtro: sin la query, el
     segundo clic no cerraria el panel. */
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return;

    /* `pointerdown` y no `click`: con `click`, un gesto que empieza dentro del
       panel y termina fuera cerraria el panel ANTES de que el enlace navegue. */
    const onPointerDown = (event: PointerEvent): void => {
      if (event.target instanceof Node && box.current?.contains(event.target) === true) return;

      setOpen(false);
    };

    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;

      setOpen(false);
      /* El foco vuelve a la campana. Si se quedara en un panel que ya no esta,
         el siguiente tabulador empezaria desde el principio de la pagina. */
      box.current?.querySelector<HTMLButtonElement>("button")?.focus();
    };

    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function toggle(): void {
    const next = !open;

    setOpen(next);

    /* Se relee al ABRIR, que es el unico momento en el que la respuesta importa.
       Un temporizador cada minuto seria una peticion por minuto y por pestana
       abierta para enterarse de algo que casi siempre cambia por lo que el
       propio usuario acaba de hacer (y eso ya invalida la lectura). */
    if (next) void notifications.refetch();
  }

  return (
    <div className="nbell" ref={box}>
      <button
        type="button"
        className="icon-btn"
        aria-label={
          unread === 0
            ? "Notificaciones, ninguna sin ver"
            : `Notificaciones, ${unread} sin ver`
        }
        title="Notificaciones"
        aria-expanded={open}
        /* Solo cuando el panel existe: `aria-controls` apuntando a un id que no
           esta en la pagina manda a un lector de pantalla a buscar la nada. */
        aria-controls={open ? "npanel" : undefined}
        onClick={toggle}
      >
        <Icon name="bell" />
        {/* El globo solo existe cuando hay algo que contar: un cero en un globo
            rojo es una alarma que dice que no hay alarma. Va oculto a los
            lectores de pantalla porque el numero ya esta en el rotulo del
            boton, y ahi se lee junto a lo que significa. */}
        {unread > 0 && (
          <span className="dot" aria-hidden="true">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <section className="panel npanel" id="npanel" aria-label="Notificaciones">
          <div className="n-h">
            <h3>Notificaciones</h3>
            {/* Marcar como visto escribe en la PROPIA campana: mirando la de otro
                no se ofrece, porque apagaria los avisos de uno mientras se leen
                los del otro. */}
            {!viewingOther && (
              <>
                <button
                  type="button"
                  disabled={unread === 0 || markSeen.isPending}
                  onClick={() => markSeen.mutate(null)}
                >
                  {markSeen.isPending ? "Marcando…" : "Marcar todo como visto"}
                </button>
                {/* Limpiar la campana entera. "Quitar" y no "Eliminar": los avisos
                    vuelven si pasa algo nuevo, y prometer borrado permanente seria
                    mentir sobre lo que hace el boton. */}
                <button
                  type="button"
                  disabled={items.length === 0 || dismiss.isPending}
                  onClick={() => dismiss.mutate(null)}
                >
                  {dismiss.isPending ? "Quitando…" : "Quitar todos"}
                </button>
              </>
            )}
          </div>

          <div className="n-list">
            {notifications.isPending ? (
              <p className="n-clear">Cargando los avisos…</p>
            ) : notifications.error !== null ? (
              <p className="n-clear">
                <Icon name="refresh" />
                <span>
                  <b>No se pudieron leer los avisos.</b>
                  Vuelve a abrir la campana para intentarlo otra vez.
                </span>
              </p>
            ) : items.length === 0 ? (
              <p className="n-clear">
                <Icon name="check" />
                <span>
                  <b>Todo al dia.</b>
                  Nada abierto sin cerrar, nada cerrado sin revisar y el diario al corriente.
                </span>
              </p>
            ) : (
              items.map((item) => (
                <Item
                  key={item.kind}
                  item={item}
                  timeZone={timeZone}
                  canDismiss={!viewingOther}
                  dismissing={dismiss.isPending}
                  onDismiss={() => dismiss.mutate(item.kind)}
                />
              ))
            )}
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * Una fila de la campana: un ENLACE que lleva a la pantalla y una X que quita el
 * aviso. Dos destinos, dos controles de verdad y HERMANOS, nunca uno dentro del
 * otro: un `<button>` metido en un `<a>` no es marcado valido, con el teclado
 * solo se alcanza uno de los dos, y pulsarlo navega -- que es exactamente el
 * fallo de las flechas de las capturas que el propietario ya reporto.
 */
function Item(props: {
  item: Notification;
  timeZone: string;
  canDismiss: boolean;
  onDismiss: () => void;
  dismissing: boolean;
}): ReactNode {
  const { item } = props;
  const meta = KINDS[item.kind];

  return (
    <div className={item.isUnread ? "n-item unread" : "n-item"}>
      <Link className="n-go" to={meta.to}>
        <span className="n-ic">
          <Icon name={meta.icon} />
        </span>
        {/* Todo con `span` y no con `p`: esta parte es un enlace, y un parrafo
            dentro de un enlace es marcado invalido. */}
        <span className="n-b">
          <b>{item.title}</b>
          <span className="n-p">{item.body}</span>
        </span>
        {/* `dateTime` con el instante entero y el texto en dias: lo primero es lo
            que una maquina puede leer, lo segundo lo que una persona necesita. */}
        <time dateTime={item.latestAtUtc}>{formatSince(item.latestAtUtc, props.timeZone)}</time>
      </Link>

      {/* "Quitar" y no "Eliminar": el aviso vuelve si pasa algo nuevo, y un verbo
          que promete borrado permanente mentiria.

          Mirando la bitacora de OTRO no se ofrece, por lo mismo que no se ofrece
          marcar como visto: lo que uno no quiere ver es suyo, y limpiarle la
          campana al dueno seria decidir por el (ADR 0005). El servidor marca
          siempre al usuario de la sesion, asi que esconderlo aqui no autoriza
          nada: solo evita ofrecer algo que haria otra cosa de la que parece. */}
      {props.canDismiss && (
        <button
          type="button"
          className="n-x"
          aria-label={`Quitar el aviso: ${item.title}`}
          title="Quitar de la lista. Vuelve si pasa algo nuevo."
          disabled={props.dismissing}
          onClick={props.onDismiss}
        >
          <Icon name="x" />
        </button>
      )}
    </div>
  );
}
